import { motion } from "framer-motion";
import { ExternalLink, Activity } from "lucide-react";
import ChartTabs from "./ChartTabs";
import { usePrice } from "../hooks/usePrice";
import { fmtPrice, fmtUsd, fmtChange } from "../lib/format";
import { BUY_URL, CHART_EMBED_URL } from "../lib/constants";

const reveal = {
  initial: { y: 36, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, amount: 0.15 },
};

const Stat = ({ label, value, sub, tone, testid }) => (
  <div
    data-testid={testid}
    className="sticker-sm rounded-2xl bg-white p-4 sm:p-5"
  >
    <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-ocean-muted">
      {label}
    </p>
    <p
      className={`mt-2 font-display text-xl font-semibold sm:text-2xl ${
        tone || ""
      }`}
    >
      {value}
    </p>
    {sub && <p className="mt-1 text-xs font-bold text-ocean-muted">{sub}</p>}
  </div>
);

export default function LiveChart() {
  const { data, isLoading } = usePrice();
  const change = fmtChange(data?.changeH24);
  const up = change && !change.startsWith("-");
  const ok = data?.ok;

  return (
    <section id="chart" data-testid="chart-section" className="relative bg-ocean-bg2 pb-24 sm:pb-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div {...reveal} className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.3em] text-ocean-cyan">
              <Activity className="h-4 w-4" />
              Live Feed
            </p>
            <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              The pulse of the pod
            </h2>
            <p className="mt-3 max-w-lg text-base font-medium text-ocean-muted sm:text-lg">
              Live price and market data, straight from the surface — refreshed
              every 20 seconds.
            </p>
          </div>
          <a
            data-testid="chart-dexscreener-link"
            href={BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-push sticker-sm flex items-center gap-2 rounded-full bg-ocean-navy px-5 py-3 font-display text-sm font-semibold text-white"
          >
            Open on DexScreener
            <ExternalLink className="h-4 w-4" />
          </a>
        </motion.div>

        <motion.div {...reveal} transition={{ ...reveal, delay: 0.1 }} className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-5">
          <Stat
            testid="stat-price"
            label="Price"
            value={ok ? fmtPrice(data.priceUsd) : "…"}
            sub={ok && data.priceNative ? `${data.priceNative} SOL` : "surfacing"}
          />
          <Stat
            testid="stat-change"
            label="24h Change"
            value={ok && change ? change : "…"}
            tone={
              up ? "text-green-600" : change ? "text-rose-600" : ""
            }
            sub={ok ? "hold on tight" : "surfacing"}
          />
          <Stat
            testid="stat-mcap"
            label="Market Cap"
            value={ok ? fmtUsd(data.marketCap ?? data.fdv) : "…"}
            sub={ok ? "whale-sized" : "surfacing"}
          />
          <Stat
            testid="stat-volume"
            label="24h Volume"
            value={ok ? fmtUsd(data.volumeH24) : "…"}
            sub={ok ? "splashing" : "surfacing"}
          />
          <Stat
            testid="stat-liquidity"
            label="Liquidity"
            value={ok ? fmtUsd(data.liquidityUsd) : "…"}
            sub={ok ? "in the pool" : "surfacing"}
          />
        </motion.div>

        <ChartTabs />
      </div>
    </section>
  );
}
